import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const getQuotationsList = createAsyncThunk(
    'quotations/getQuotationsList',
    async ({ accountId, token, search, start, length, statusCode, userId }, { rejectWithValue }) => {
    try {
        let url = `${import.meta.env.VITE_API_BASE_URL}/quotations/list?accountId=${accountId}&start=${start}&length=${length}&draw=1`;

        if (search && search.length > 0) {
            url += `&search[value]=${encodeURIComponent(search)}`;
        } else {
            url += `&search[value]=`;
        }

        if (statusCode) {
            url += `&statusCode=${statusCode}`;
        }

        if (userId) {
            url += `&userId=${userId}`;
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei preventivi');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getQuotationDetails = createAsyncThunk(
    'quotations/getQuotationDetails',
    async ({ quotationId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/quotations?quotationId=${quotationId}`;
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei dettagli del preventivo');
        }

        const data = await response.json();
        return data.data || data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const createQuotation = createAsyncThunk(
    'quotations/createQuotation',
    async ({ quotation, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/quotations`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ quotation })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante la creazione del preventivo');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const updateQuotation = createAsyncThunk(
    'quotations/updateQuotation',
    async ({ quotation, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/quotations`;
        const response = await fetch(url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ quotation })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante l\'aggiornamento del preventivo');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const updateQuotationStatus = createAsyncThunk(
    'quotations/updateQuotationStatus',
    async ({ quotationId, statusCode, statusLabel, statusColor, rejectedReason, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/quotations/updateStatus`;
        const response = await fetch(url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ quotationId, statusCode, statusLabel, statusColor, rejectedReason })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante l\'aggiornamento dello stato');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const deleteQuotation = createAsyncThunk(
    'quotations/deleteQuotation',
    async ({ quotationId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/quotations?quotationId=${quotationId}`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante l\'eliminazione del preventivo');
        }
        return quotationId;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const createQuotationFromLead = createAsyncThunk(
    'quotations/createQuotationFromLead',
    async ({ leadId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/quotations/createFromLead`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ leadId })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante la creazione del preventivo dalla lead');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

const quotationsSlice = createSlice({
    name: 'quotations',
    initialState: {
        data: null,
        loading: false,
        error: null,
        quotationDetails: null,
        loadingDetails: false,
        errorDetails: null,
        createLoading: false,
        createError: null,
        updateLoading: false,
        updateError: null,
        deleteLoading: false,
        deleteError: null,
    },
    reducers: {
        resetQuotationDetails: (state) => {
            state.quotationDetails = null;
            state.loadingDetails = false;
            state.errorDetails = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // getQuotationsList
            .addCase(getQuotationsList.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getQuotationsList.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(getQuotationsList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload || 'Errore durante il recupero dei preventivi';
            })
            // getQuotationDetails
            .addCase(getQuotationDetails.pending, (state) => {
                state.loadingDetails = true;
                state.errorDetails = null;
            })
            .addCase(getQuotationDetails.fulfilled, (state, action) => {
                state.loadingDetails = false;
                state.quotationDetails = action.payload;
            })
            .addCase(getQuotationDetails.rejected, (state, action) => {
                state.loadingDetails = false;
                state.errorDetails = action.payload || 'Errore durante il recupero dei dettagli del preventivo';
            })
            // createQuotation
            .addCase(createQuotation.pending, (state) => {
                state.createLoading = true;
                state.createError = null;
            })
            .addCase(createQuotation.fulfilled, (state) => {
                state.createLoading = false;
            })
            .addCase(createQuotation.rejected, (state, action) => {
                state.createLoading = false;
                state.createError = action.payload;
            })
            // updateQuotation
            .addCase(updateQuotation.pending, (state) => {
                state.updateLoading = true;
                state.updateError = null;
            })
            .addCase(updateQuotation.fulfilled, (state) => {
                state.updateLoading = false;
            })
            .addCase(updateQuotation.rejected, (state, action) => {
                state.updateLoading = false;
                state.updateError = action.payload;
            })
            // updateQuotationStatus
            .addCase(updateQuotationStatus.pending, (state) => {
                state.updateLoading = true;
                state.updateError = null;
            })
            .addCase(updateQuotationStatus.fulfilled, (state, action) => {
                state.updateLoading = false;
                state.quotationDetails = action.payload;
            })
            .addCase(updateQuotationStatus.rejected, (state, action) => {
                state.updateLoading = false;
                state.updateError = action.payload;
            })
            // deleteQuotation
            .addCase(deleteQuotation.pending, (state) => {
                state.deleteLoading = true;
                state.deleteError = null;
            })
            .addCase(deleteQuotation.fulfilled, (state) => {
                state.deleteLoading = false;
            })
            .addCase(deleteQuotation.rejected, (state, action) => {
                state.deleteLoading = false;
                state.deleteError = action.payload;
            })
            // createQuotationFromLead
            .addCase(createQuotationFromLead.pending, (state) => {
                state.createLoading = true;
                state.createError = null;
            })
            .addCase(createQuotationFromLead.fulfilled, (state) => {
                state.createLoading = false;
            })
            .addCase(createQuotationFromLead.rejected, (state, action) => {
                state.createLoading = false;
                state.createError = action.payload;
            });
    }
});

export const { resetQuotationDetails } = quotationsSlice.actions;

export const selectQuotationsData = (state) => state.quotations.data;
export const selectQuotationsLoading = (state) => state.quotations.loading;
export const selectQuotationsError = (state) => state.quotations.error;
export const selectQuotationDetails = (state) => state.quotations.quotationDetails;
export const selectQuotationDetailsLoading = (state) => state.quotations.loadingDetails;
export const selectQuotationDetailsError = (state) => state.quotations.errorDetails;
export const selectQuotationCreateLoading = (state) => state.quotations.createLoading;
export const selectQuotationCreateError = (state) => state.quotations.createError;
export const selectQuotationUpdateLoading = (state) => state.quotations.updateLoading;
export const selectQuotationUpdateError = (state) => state.quotations.updateError;
export const selectQuotationDeleteLoading = (state) => state.quotations.deleteLoading;
export const selectQuotationDeleteError = (state) => state.quotations.deleteError;

export default quotationsSlice.reducer;
